import { Drawer, Tag, Button, Divider } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";

export interface InvestigatorProfile {
  inv_key: string;
  name: string;
  degree: string;
  specialty: string;
  institution: string;
  /** Cross-trial site-profile key of their most frequent mastered institution. */
  institution_key?: string | null;
  /** The site they currently work at (mastered primary site) — empty when they
   *  never mastered to one. `site_key` is its site-profile key (golden id). */
  site_key?: string | null;
  site_name?: string;
  site_city?: string;
  site_country?: string;
  city: string;
  country: string;
  region: string;
  years_experience: number;
  trial_count?: number;
  site_count?: number;
  total_enrolled?: number;
  therapeutic_areas: string[];
  trials: Array<{
    trial_id: string;
    site_id: string;
    trial_name: string | null;
    phase: string | null;
    status: string;
    statusColor: string;
    role: string | null;
    site_name: string | null;
    sponsor: string | null;
    enrolled: number | null;
    target: number | null;
    tenure_start?: string | null;
    tenure_end?: string | null;
  }>;
}

interface InvestigatorProfileDrawerProps {
  profile: InvestigatorProfile | null;
  onClose: () => void;
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="text-[13px] text-[#6B7280] shrink-0">{label}</span>
      <span className="text-[13px] font-medium text-[#1F2933] text-right">
        {value}
      </span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="py-0.5">
      <p className="text-[12px] text-[#6B7280] leading-snug mb-0.5 m-0">
        {label}
      </p>
      <p className="text-[14px] text-[#1F2933] font-medium leading-snug m-0">
        {value}
      </p>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[12px] font-semibold uppercase tracking-wide text-[#6B7280] mb-2 m-0">
      {children}
    </p>
  );
}

export default function InvestigatorProfileDrawer({
  profile,
  onClose,
}: InvestigatorProfileDrawerProps) {
  const navigate = useNavigate();
  const profileHidden = useFeatureFlag(
    FEATURE_FLAGS.INVESTIGATOR_PROFILE_LINKS_HIDDEN,
  );

  return (
    <Drawer
      title={
        profile ? (
          <div className="pr-2">
            <p className="text-[16px] font-bold text-white leading-snug m-0">
              {profile.name}
              {profile.degree ? `, ${profile.degree}` : ""}
            </p>
            <p className="text-[13px] font-normal text-white/60 mt-0.5 m-0">
              {profile.specialty} · {profile.region}
            </p>
          </div>
        ) : null
      }
      placement="right"
      width={420}
      open={!!profile}
      onClose={onClose}
      destroyOnHidden
      footer={
        profileHidden ? null : (
          <div className="flex justify-end">
            <Button
              type="primary"
              icon={<LinkOutlined />}
              className="bg-[#0C98D0] border-none"
              onClick={() => {
                onClose();
                navigate(`/portfolio/investigators/${profile?.inv_key}`);
              }}
            >
              Open Full Profile
            </Button>
          </div>
        )
      }
    >
      {profile && (
        <div>
          {/* Attribute grid */}
          <div className="mb-5 rounded-[8px] border border-[#F0F2F4] divide-y divide-[#F0F2F4] px-3">
            {profile.institution && (
              <MetaRow label="Institution" value={profile.institution} />
            )}
            <MetaRow
              label="Location"
              value={`${profile.city}, ${profile.country}`}
            />
            {profile.years_experience != null &&
              profile.years_experience > 0 && (
                <MetaRow
                  label="Experience"
                  value={`${profile.years_experience} years`}
                />
              )}
            {profile.trial_count != null && (
              <MetaRow label="Trials" value={profile.trial_count} />
            )}
            {profile.site_count != null && (
              <MetaRow label="Sites" value={profile.site_count} />
            )}
            {profile.total_enrolled != null && profile.total_enrolled > 0 && (
              <MetaRow label="Total enrolled" value={profile.total_enrolled} />
            )}
          </div>

          {/* Therapeutic areas */}
          {profile.therapeutic_areas.length > 0 && (
            <div className="mb-5">
              <SectionLabel>Therapeutic Areas</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {profile.therapeutic_areas.map((ta) => (
                  <Tag key={ta} className="text-[13px] m-0">
                    {ta}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {/* Trials */}
          {profile.trials && profile.trials.length > 0 && (
            <>
              <Divider className="my-4" />
              <SectionLabel>Trials ({profile.trials.length})</SectionLabel>
              <div className="flex flex-col gap-3">
                {profile.trials.map((t, i) => (
                  <div
                    key={`${t.trial_id}-${i}`}
                    className="rounded-[8px] border border-[#F0F2F4] bg-[#FAFAFA] p-3"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-[13px] font-semibold text-[#1F2933] m-0 leading-snug">
                          {t.trial_name ?? t.trial_id}
                        </p>
                        <p className="text-[12px] text-[#6B7280] m-0 mt-0.5">
                          {t.trial_id}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 inline-flex items-center h-5 rounded-[4px] px-1.5 text-[12px] font-semibold ${t.status === "Active" ? "bg-[#ECFDF5] text-[#065F46]" : "bg-[#F3F4F6] text-[#6B7280]"}`}
                      >
                        {t.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {t.role && <Stat label="Role" value={t.role} />}
                      {t.phase && (
                        <Stat label="Phase" value={`Phase ${t.phase}`} />
                      )}
                      {t.site_name && <Stat label="Site" value={t.site_name} />}
                      {t.enrolled != null && (
                        <Stat
                          label="Enrolled"
                          value={
                            t.target != null
                              ? `${t.enrolled} / ${t.target}`
                              : String(t.enrolled)
                          }
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </Drawer>
  );
}
